import { createFileRoute } from "@tanstack/react-router";
import {
  IndustryMarketingPage,
  industryMarketingHead,
  type IndustryFAQ,
} from "@/components/IndustryMarketingPage";

const FAQS: IndustryFAQ[] = [
  {
    q: "How do roofing companies get more leads in Colorado?",
    a: "The fastest path is a tuned Google Business Profile + local SEO ranking you for 'roof replacement Colorado Springs', 'storm damage roofer Denver', and 'metal roofing Colorado'. We layer Google Local Service Ads for emergency calls and pair the site with hail-storm content that converts insurance-driven traffic. Full breakdown: /answers/how-roofing-companies-get-more-leads-colorado.",
  },
  {
    q: "What's the best marketing channel for a Colorado roofing contractor?",
    a: "Local SEO + Google Business Profile is the highest-ROI channel for most Colorado roofers because hail and wind events drive concentrated bursts of search demand. Google Ads + Local Service Ads layer in for storm chasing windows. Door-knocking and social media are secondary — the searches are already happening, you just need to rank.",
  },
  {
    q: "How long does roofing SEO take to work in Colorado?",
    a: "Most Colorado roofing companies see meaningful Map Pack movement in 60–120 days and stronger organic lead flow within 4–6 months. The Front Range is competitive (Denver, Aurora, Colorado Springs), so consistent monthly review velocity and storm-content publishing is what compounds.",
  },
  {
    q: "How do I rank for hail damage and storm work in Colorado?",
    a: "Build dedicated landing pages for each storm-prone city (Aurora, Highlands Ranch, Colorado Springs, Castle Rock, Loveland) with hyper-local hail history, insurance claim guidance, and FAQs about adjuster meetings. Combine that with rapid GBP posts after every named storm and a steady review cadence from completed jobs.",
  },
  {
    q: "Should I run Google Ads or focus on SEO first for my roofing business?",
    a: "If you need leads this week, start Google Local Service Ads (LSAs) — they pay per lead, not per click, and surface above the Map Pack. SEO is the long-term lead engine that doesn't shut off when you cut spend. Most successful Colorado roofers run LSAs + SEO simultaneously, with traditional Google Ads only during storm-response windows.",
  },
];

export const Route = createFileRoute("/roofing-marketing-colorado")({
  head: () =>
    industryMarketingHead("Roofing", "roofing", "roofing-marketing-colorado", FAQS),
  component: () => (
    <IndustryMarketingPage
      industry="Roofing"
      industryLower="roofing"
      slug="roofing-marketing-colorado"
      subheadline="We help Colorado roofers convert hail storms, wind events, and re-roof demand into booked inspections — across the Front Range and beyond."
      painPoints={[
        "Storm season hits and the phone rings — for the storm chasers, not your local crew",
        "GBP is half-finished and your reviews lag the door-knockers",
        "Insurance work pages all blur together — nothing ranks for 'hail damage [city]'",
      ]}
      serviceDescriptions={{
        seo: "Rank for re-roofs, repairs, hail damage, metal roofing, and TPO across every Colorado city you actually service.",
        googleMaps: "Own the Map Pack so 'roofer near me' calls go to your team — not to chasers from out of state.",
        webDesign: "Fast, mobile-first roofing sites with real before/after photo galleries and clear insurance claim guidance.",
        ppc: "Profitable Google Local Service Ads + Google Ads timed to storm windows and re-roof season.",
        geoAi: "Get cited when Coloradans ask ChatGPT 'who's the best roofer in [my town]' or 'how do I file a hail claim'.",
        socialMedia: "Real job-site content from active roofs that builds trust faster than any sales page or door knock.",
      }}
      testimonial={{
        quote:
          "The May hail storm used to be a footrace against the chasers. This year we owned the Map Pack from day one — booked solid through August before the chasers even hit our zip codes.",
        role: "Roofing Contractor",
        city: "Colorado Springs",
      }}
      faqs={FAQS}
      industrySpokes={[
        { to: "/roofing-seo-colorado", label: "Roofing SEO in Colorado", description: "Deep-dive on how we rank Colorado roofers for re-roof, repair, and hail-damage searches." },
        { to: "/roofing-seo-colorado-springs", label: "Roofing SEO in Colorado Springs", description: "Springs-specific roofing SEO playbook — from Briargate to Falcon to the Black Forest." },
      ]}
      cityHubLinks={[
        { to: "/colorado-springs", label: "Roofing marketing in Colorado Springs", description: "Help Springs roofers own the Map Pack from Briargate to Falcon and the Black Forest hail corridor." },
        { to: "/denver-digital-marketing", label: "Roofing marketing in Denver", description: "Compete in metro Denver's hail-driven roofing market — neighborhood by neighborhood." },
        { to: "/aurora-seo", label: "Roofing marketing in Aurora", description: "Aurora is one of the most hail-prone zip codes in the country — we build for that storm cycle." },
      ]}
    />
  ),
});
